"use client";
import type React from "react"
import { useState, useRef, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import CourseDetailsStep from "./steps/CourseDetailsStep"
import CurriculumStep from "./steps/CurriculumStep"
import CertificateStep from "./steps/CertificateStep"
import PaymentDetailsStep, { PaymentDetailsStepRef } from "./steps/PaymentDetailsStep"
import AdditionalDetailsStep, { AdditionalDetailsStepRef } from "./steps/AdditionalDetailsStep"
import useAuth from "../../hooks/useAuth"
import { updateCourse, getCourseById, getCoursePriceByCourseId, getCourseAdditionalByCourseId, getCourseCertificatesByCourseId } from "../../api/courseService"
import Modal from "../../components/common/Modal"
import { ArrowLeft, Check, Save } from "lucide-react"

interface CourseData {
  title: string;
  description: string;
  language: string;
  coverImage: File | null;
  availableOnDesktop: boolean;
  modules: any[];
  certificates: any[];
  pricing: any;
  requirements: string[];
  additional: any;
  _id: string | null;
  thumbnail: string | null;
  courseType: 'Course' | 'Webinar';
  webinarChannel: string | null;
  certificateEnable?: boolean; 
}

const EditCourse: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [currentStep, setCurrentStep] = useState(1)
  const [courseData, setCourseData] = useState<CourseData>({
    title: "",
    description: "",
    language: "English",
    coverImage: null,
    availableOnDesktop: true,
    modules: [],
    certificates: [],
    pricing: {},
    requirements: [],
    additional: {},
    _id: null,
    thumbnail: null,
    courseType: "Course",
    webinarChannel: null,
    certificateEnable: false
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const { user } = useAuth()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [fetchingCourse, setFetchingCourse] = useState(true)
  const [fetchingAdditional, setFetchingAdditional] = useState(false)
  
  const certificateStepRef = useRef<{ saveCertificate: () => Promise<boolean> }>(null)
  const paymentDetailsStepRef = useRef<PaymentDetailsStepRef>(null)
  const additionalDetailsStepRef = useRef<AdditionalDetailsStepRef>(null)

  const [isStep1Dirty, setIsStep1Dirty] = useState(false)
  const [isCurriculumDirty, setIsCurriculumDirty] = useState(false)
  const [isLessonFormDirty, setIsLessonFormDirty] = useState(false)
  const [isCertificateDirty, setIsCertificateDirty] = useState(false)
  const [isAdditionalDirty, setIsAdditionalDirty] = useState(false)


  const steps = [
    { id: 1, title: "Course details", completed: currentStep > 1, current: currentStep === 1 },
    { id: 2, title: "Curriculum", completed: currentStep > 2, current: currentStep === 2 },
    { id: 3, title: "Certificate", completed: currentStep > 3, current: currentStep === 3 },
    { id: 4, title: "Payment details", completed: currentStep > 4, current: currentStep === 4 },
    { id: 5, title: "Additional details", completed: currentStep > 5, current: currentStep === 5 },
  ]

  const [originalCourseData, setOriginalCourseData] = useState({
    title: "",
    description: "",
    language: "English",
    availableOnDesktop: true,
    courseType: "Course" as 'Course' | 'Webinar',
    webinarChannel: null as string | null,
    thumbnail: null as string | null,
  })

  // Fetch course data on component mount
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!id) return
      
      setFetchingCourse(true)
      try {
        const response = await getCourseById(id)
        let pricingData = null;
        let certificatesData = null;
        
        if (response.status === 200 && response.data) {
          // Fetch pricing data using the API you provided
          try {
            const pricingResponse = await getCoursePriceByCourseId(id);
            if (pricingResponse.status === 200 && pricingResponse.data) {
              pricingData = pricingResponse.data;
              console.log("Fetched pricing data:", pricingData);
            }
          } catch (pricingError) {
            console.error("Error fetching pricing data:", pricingError);
            // It's okay if pricing data doesn't exist yet
          }
          
          // Fetch certificates data
          try {
            const certificatesResponse = await getCourseCertificatesByCourseId(id);
            if (certificatesResponse.status === 200 && certificatesResponse.data) {
              certificatesData = certificatesResponse.data;
              console.log("Fetched certificates data:", certificatesData);
            }
          } catch (certificatesError) {
            console.error("Error fetching certificates data:", certificatesError);
            // It's okay if certificates data doesn't exist yet
          }
          
          const fetchedData = response.data;
          setCourseData({
            title: fetchedData.title,
            description: fetchedData.description,
            language: fetchedData.language,
            coverImage: null,
            availableOnDesktop: fetchedData.desktopAvailable,
            modules: [],
            certificates: certificatesData || [],
            pricing: pricingData, // Include pricing data
            requirements: [],
            additional: {},
            _id: fetchedData._id,
            thumbnail: fetchedData.thumbnail,
            courseType: fetchedData.courseType || 'Course',
            webinarChannel: fetchedData.webinarChannel || null,
            certificateEnable: fetchedData.certificateEnable ?? false,
          })

setOriginalCourseData({
  title: fetchedData.title || "",
  description: fetchedData.description || "",
  language: fetchedData.language || "English",
  availableOnDesktop: fetchedData.desktopAvailable ?? true,
  courseType: fetchedData.courseType || "Course",
  webinarChannel: fetchedData.webinarChannel || null,
  thumbnail: fetchedData.thumbnail || null
})

        } else {
          setError(response.message || "Failed to fetch course data")
        }
      } catch (error: any) {
        setError(error.message || "Error fetching course data")
      } finally {
        setFetchingCourse(false)
      }
    }
    
    fetchCourseData()
  }, [id])

  

  // Fetch additional data when step 5 is reached
  useEffect(() => {
    if (currentStep === 5 && courseData._id) {
      const fetchAdditionalData = async () => {
        setFetchingAdditional(true)
        try {
          console.log("Fetching additional data for course:", courseData._id);
          // Add null check before calling the function
          if (courseData._id) {
            const response = await getCourseAdditionalByCourseId(courseData._id);
            if (response.status === 200 && response.data) {
              console.log("Fetched additional data:", response.data);
              setCourseData(prev => ({
                ...prev,
                additional: response.data
              }));
            }
          }
        } catch (error: any) {
          console.error("Error fetching additional data:", error);
          setError(error.message || "Failed to fetch additional details");
        } finally {
          setFetchingAdditional(false)
        }
      };
      
      fetchAdditionalData();
    }
  }, [currentStep, courseData._id]);

  // Re-fetch course data when ID changes or when returning to previous steps
  useEffect(() => {
    if (id && !fetchingCourse) {
      const refreshData = async () => {
        try {
          const response = await getCourseById(id)
          let pricingData: any = null;
          let certificatesData: any = null;
          
          if (response.status === 200 && response.data) {
            // Fetch pricing data
            try {
              const pricingResponse = await getCoursePriceByCourseId(id);
              if (pricingResponse.status === 200 && pricingResponse.data) {
                pricingData = pricingResponse.data;
                console.log("Refreshed pricing data:", pricingData);
              }
            } catch (pricingError) {
              console.error("Error fetching updated pricing data:", pricingError);
            }
            
            // Fetch certificates data
            try {
              const certificatesResponse = await getCourseCertificatesByCourseId(id);
              if (certificatesResponse.status === 200 && certificatesResponse.data) {
                certificatesData = certificatesResponse.data;
                console.log("Refreshed certificates data:", certificatesData);
              }
            } catch (certificatesError) {
              console.error("Error fetching updated certificates data:", certificatesError);
            }
            
            const fetchedData = response.data;
            setCourseData(prev => ({
              ...prev,
              title: fetchedData.title,
              description: fetchedData.description,
              language: fetchedData.language,
              availableOnDesktop: fetchedData.desktopAvailable,
              pricing: pricingData, // Update with refreshed pricing data
              certificates: certificatesData || [], // Update with refreshed certificates data
              thumbnail: fetchedData.thumbnail,
              courseType: fetchedData.courseType || 'Course',
              webinarChannel: fetchedData.webinarChannel || null
            }))
          }
        } catch (error: any) {
          console.error("Error refreshing course data:", error);
        }
      }
      
      refreshData()
    }
  }, [id, fetchingCourse])

useEffect(() => {
  window.scrollTo(0, 0)
}, [currentStep])
  
  const handleNext = () => {
    if (currentStep === 2) {
      const hasContent = courseData.modules?.some(module => 
        module.lessons && Array.isArray(module.lessons) && module.lessons.length > 0
      );
      
      // Also check if any module has lessons with a length property
      const hasLegacyContent = courseData.modules?.some(module => 
        module.lessons && module.lessons.length > 0
      );
      
      if (!hasContent && !hasLegacyContent) {
        setError("Please create at least one lesson, quiz, or document in a module before proceeding to the next step.");
        return;
      }
    }
    
    if (currentStep < 5) {
      setError(null)
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = async () => {
    if (currentStep === 4 && paymentDetailsStepRef.current) {
      // Check if there are unsaved changes
      if (paymentDetailsStepRef.current.hasUnsavedChanges()) {
        const shouldSave = window.confirm(
          "You have unsaved changes in the payment details. Do you want to save them before going back?"
        );
        
        if (shouldSave) {
          try {
            setLoading(true);
            await paymentDetailsStepRef.current.savePaymentDetails();
          } catch (error: any) {
            setError(error.message);
            setLoading(false);
            return;
          } finally {
            setLoading(false);
          }
        }
      }
    }
    
    // Fetch certificate data when going back to certificate step
    if (currentStep === 4 && courseData._id) {
      try {
        setLoading(true);
        const certificatesResponse = await getCourseCertificatesByCourseId(courseData._id);
        if (certificatesResponse.status === 200 && certificatesResponse.data) {
          setCourseData(prev => ({
            ...prev,
            certificates: certificatesResponse.data,
          }));
        }
      } catch (error: any) {
        console.error("Error fetching certificate data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
    
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
  try {
    setLoading(true)
    setError(null)

    if (!courseData._id) {
      setError("Course ID is missing")
      return
    }

    const response = await updateCourse({
      id: courseData._id,
      title: courseData.title,
      user_id: user?._id || "",
      description: courseData.description,
      language: courseData.language,
      desktopAvailable: courseData.availableOnDesktop,
      file: courseData.coverImage || undefined,
      courseType: courseData.courseType,
      webinarChannel:
        courseData.courseType === "Webinar"
          ? courseData.webinarChannel
          : null
    })

    if (response.status >= 400) {
      setError(response.message || "Failed to update course")
      return
    }

    // Re-fetch updated course
    const updatedCourseResponse = await getCourseById(courseData._id)

    if (
      updatedCourseResponse.status === 200 &&
      updatedCourseResponse.data
    ) {
      const fetchedData = updatedCourseResponse.data

      setCourseData(prev => ({
        ...prev,
        title: fetchedData.title,
        description: fetchedData.description,
        language: fetchedData.language,
        availableOnDesktop: fetchedData.desktopAvailable,
        thumbnail: fetchedData.thumbnail,
        courseType: fetchedData.courseType || "Course",
        webinarChannel: fetchedData.webinarChannel || null,
        coverImage: null
      }))

      setOriginalCourseData({
        title: fetchedData.title || "",
        description: fetchedData.description || "",
        language: fetchedData.language || "English",
        availableOnDesktop: fetchedData.desktopAvailable ?? true,
        courseType: fetchedData.courseType || "Course",
        webinarChannel: fetchedData.webinarChannel || null,
        thumbnail: fetchedData.thumbnail || null
      })
    }

    setIsStep1Dirty(false)

    setSuccessMessage("Course updated successfully!")

    setTimeout(() => {
      setSuccessMessage(null)
    }, 1000)

    // Go to next step after successful update
    if (currentStep === 1) {
      setCurrentStep(2)
    }

  } catch (error: any) {
    setError(
      error.message || "Failed to update course. Please try again."
    )
  } finally {
    setLoading(false)
  }
}

//  const handleCertificateNext = async () => {
//   try {
//     setLoading(true);
//     setError(null);

//     if (!courseData._id) {
//       throw new Error("Course ID is missing");
//     }

//     const certificateEnabled = courseData.enableCertificate;

//     if (certificateStepRef.current) {
//       await certificateStepRef.current.saveCertificate();
//     }

//     // Save certificate ON/OFF setting at course level
//     const courseUpdateResponse = await updateCourse({
//       id: courseData._id,
//       enableCertificate: certificateEnabled,
//     });

//     if (courseUpdateResponse.status >= 400) {
//       throw new Error(
//         courseUpdateResponse.message ||
//           "Failed to update certificate setting"
//       );
//     }

//     // Refresh certificate details for UI only
//     const certificatesResponse =
//       await getCourseCertificatesByCourseId(courseData._id);

//     setCourseData(prev => ({
//       ...prev,
//       certificates:
//         certificatesResponse.status === 200 &&
//         certificatesResponse.data
//           ? certificatesResponse.data
//           : prev.certificates,

//       // NEVER calculate this from certificates.length
//       enableCertificate: certificateEnabled,
//     }));

//     setIsCertificateDirty(false);

//     handleNext();
//   } catch (err) {
//     console.error("Error saving certificate:", err);

//     setError(
//       err instanceof Error
//         ? err.message
//         : "Failed to save certificate"
//     );
//   } finally {
//     setLoading(false);
//   }
// };

// const handleCertificateNext = async () => {
//   try {
//     setLoading(true);
//     setError(null);

//     if (!courseData._id) {
//       throw new Error("Course ID is missing");
//     }


//     if (certificateStepRef.current) {
//       await certificateStepRef.current.saveCertificate();
//     }
 
//     const certificatesResponse =
//       await getCourseCertificatesByCourseId(courseData._id);

//     setCourseData(prev => ({
//       ...prev,
//       certificates:
//         certificatesResponse.status === 200 &&
//         certificatesResponse.data
//           ? certificatesResponse.data
//           : prev.certificates,

//     }));

//     setIsCertificateDirty(false);

//     handleNext();
//   } catch (err) {
//     console.error("Error saving certificate:", err);

//     setError(
//       err instanceof Error
//         ? err.message
//         : "Failed to save certificate"
//     );
//   } finally {
//     setLoading(false);
//   }
// };

const handleCertificateNext = async () => {
  try {
    setLoading(true);
    setError(null);

    if (!courseData._id) {
      throw new Error("Course ID is missing");
    }

    // 1. Save certificate details + get current ON/OFF state
    const certificateEnabled =
      certificateStepRef.current
        ? await certificateStepRef.current.saveCertificate()
        : false;

    // 2. Save enableCertificate at COURSE level
    const courseUpdateResponse = await updateCourse({
      id: courseData._id,
      user_id: user?._id || "",
      certificateEnable: certificateEnabled,
    });

    if (courseUpdateResponse.status >= 400) {
      throw new Error(
        courseUpdateResponse.message ||
        "Failed to update certificate setting"
      );
    }

    // 3. Refresh certificate data only for UI
    const certificatesResponse =
      await getCourseCertificatesByCourseId(courseData._id);

    setCourseData(prev => ({
      ...prev,
      certificates:
        certificatesResponse.status === 200 &&
        certificatesResponse.data
          ? certificatesResponse.data
          : prev.certificates,
      certificateEnable: certificateEnabled,
    }));

    setIsCertificateDirty(false);

    handleNext();

  } catch (err) {
    console.error("Error saving certificate:", err);

    setError(
      err instanceof Error
        ? err.message
        : "Failed to save certificate"
    );
  } finally {
    setLoading(false);
  }
};

  const handlePaymentDetailsNext = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (paymentDetailsStepRef.current) {
        await paymentDetailsStepRef.current.savePaymentDetails()
        
        // Re-fetch the updated pricing data
        if (courseData._id) {
          try {
            const pricingResponse = await getCoursePriceByCourseId(courseData._id);
            if (pricingResponse.status === 200 && pricingResponse.data) {
              setCourseData(prev => ({
                ...prev,
                pricing: pricingResponse.data
              }));
            }
          } catch (pricingError) {
            console.error("Error fetching updated pricing data:", pricingError);
          }
        }
        
        handleNext()
      } else {
        handleNext()
      }
    } catch (err) {
      console.error("Error saving payment details:", err)
      setError(err instanceof Error ? err.message : "Failed to save payment details")
    } finally {
      setLoading(false)
    }
  }


  const handleAdditionalDetailsSave = async () => {
  try {
    setLoading(true)
    setError(null)

    if (!additionalDetailsStepRef.current) {
      throw new Error("Additional details step is not available")
    }

    await additionalDetailsStepRef.current.saveAdditionalDetails("Draft")

  } catch (err: any) {
    console.error("Error saving additional details:", err)

    setError(
      err?.message || "Failed to save additional details"
    )
  } finally {
    setLoading(false)
  }
}

  const handleGoToDashboard = () => {
    setShowSuccessModal(false)
    navigate("/courses")
  }

useEffect(() => {
  const isChanged =
    courseData.title !== originalCourseData.title ||
    courseData.description !== originalCourseData.description ||
    courseData.language !== originalCourseData.language ||
    courseData.availableOnDesktop !== originalCourseData.availableOnDesktop ||
    courseData.courseType !== originalCourseData.courseType ||
    courseData.webinarChannel !== originalCourseData.webinarChannel ||
    courseData.coverImage !== null

  setIsStep1Dirty(isChanged)
}, [
  courseData.title,
  courseData.description,
  courseData.language,
  courseData.availableOnDesktop,
  courseData.courseType,
  courseData.webinarChannel,
  courseData.coverImage,
  originalCourseData
])


  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CourseDetailsStep 
            key={`step1-${courseData._id}`}
            data={courseData} 
            onChange={setCourseData} 
            onNext={handleNext}
            setLoading={setLoading}
            setError={setError}
            userId={user?._id || ""}
          />
        )
      case 2:
        return (        
        <CurriculumStep 
          key={`step2-${courseData._id}`}
          data={courseData} 
          onChange={setCourseData} 
          onNext={handleNext}
          isEditMode={true}
          onDirtyChange={setIsCurriculumDirty}
          onLessonFormDirtyChange={setIsLessonFormDirty}
        />
        )
      case 3:
        return (
          <CertificateStep 
            key={`step3-${courseData._id}`}
            ref={certificateStepRef}
            data={courseData} 
            onChange={setCourseData}
            onNext={handleNext}
            setLoading={setLoading}
            setError={setError}
            isEditMode={true}
            onDirtyChange={setIsCertificateDirty}
          />
        )
      case 4:
        return (
          <PaymentDetailsStep 
            key={`step4-${courseData._id}-${courseData.pricing?._id || 'no-pricing'}`}
            ref={paymentDetailsStepRef}
            data={courseData} 
            onChange={setCourseData}
            isEditMode={true}
          />
        )
      case 5:
        return (
          <AdditionalDetailsStep 
            key={`step5-${courseData._id}`}
            ref={additionalDetailsStepRef}
            data={courseData} 
            onChange={setCourseData}
            onNavigateAfterSave={() => navigate("/courses")}
            onDirtyChange={setIsAdditionalDirty}
            isEditMode={true}
          />
        )
      default:
        return <CourseDetailsStep 
          key={`step1-${courseData._id}`}
          data={courseData} 
          onChange={setCourseData} 
          onNext={handleNext} 
          setLoading={setLoading} 
          setError={setError} 
          userId={user?._id || ""} 
        />
    }
  }

  if (fetchingCourse) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course data...</p>
        </div>
      </div>
    )
  }

  // console.log("EDIT COURSE CURRENT STEP:", currentStep);

  return (    
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-6 py-3 shadow-sm">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate("/courses")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <ArrowLeft className="mr-1 sm:mr-2" size={16} />
            <span className="hidden sm:inline text-sm">Back to courses</span>
            <span className="sm:hidden text-sm">Back</span>
          </button>
        </div>
      </div>
      
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-3">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900">Progress</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {Math.round((currentStep / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="relative">
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <div
                    className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                      step.completed
                        ? "bg-green-500 text-white shadow-md"
                        : step.current
                        ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-200"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {step.completed ? (
                      <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <span className={`mt-1 sm:mt-2 text-xs font-medium text-center max-w-12 sm:max-w-16 lg:max-w-none leading-tight ${
                    step.current ? "text-blue-600" : "text-gray-500"
                  }`}>
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 h-0.5 bg-gray-200 -z-0">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500 ease-out"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>      
      
      <div className="max-w-4xl mx-auto p-3 sm:p-4 lg:p-6">
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Edit Course</h1>
              <p className="text-gray-600">Update your course information and settings</p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-red-800">Error occurred</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}
            
            {successMessage && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-800">Success!</h4>
                  <p className="text-sm text-green-700 mt-1">{successMessage}</p>
                </div>
              </div>
            )}
            
            {fetchingAdditional && currentStep === 5 && (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-blue-100 rounded-full p-1 flex-shrink-0">
                  <svg className="animate-spin w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-blue-800">Loading additional details</h4>
                  <p className="text-sm text-blue-700 mt-1">Fetching existing additional information...</p>
                </div>
              </div>
            )}
            
            {renderCurrentStep()}
            
            <div className="flex flex-col sm:flex-row justify-between mt-8 pt-6 space-y-3 sm:space-y-0">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-medium ${
                  currentStep === 1 ? "invisible" : ""
                } w-full sm:w-auto justify-center flex items-center space-x-2`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back</span>
              </button>
              
<div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">

  {/* STEP 1 */}
  {currentStep === 1 && (
    <>
     

      <button
        onClick={() => {
          if (isStep1Dirty) {
            handleSave()
          } else {
            handleNext()
          }
        }}
        disabled={loading}
        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center space-x-2"
      >
        {loading ? (
          <>
            <svg
              className="animate-spin w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 718-8V0C5.373 5.373 0 0 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Updating...</span>
          </>
        ) : (
          <>
            <span>
              {isStep1Dirty ? "Update & Next" : "Continue"}
            </span>

            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </>
        )}
      </button>
    </>
  )}

  {/* STEP 2 */}
  {currentStep === 2 && (
    <button
      onClick={handleNext}
      disabled={loading || isLessonFormDirty}
      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center space-x-2"
    >
      <span>
        {isCurriculumDirty ? "Update & Next" : "Continue"}
      </span>

      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          stroke="currentColor"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>
  )}

  {/* STEP 3 */}
  {currentStep === 3 && (
    <button
      onClick={() => {
        if (isCertificateDirty) {
          handleCertificateNext()
        } else {
          handleNext()
        }
      }}
      disabled={loading}
      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center space-x-2"
    >
      {loading ? (
        <>
          <svg
            className="animate-spin w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
          </svg>
          <span>Saving...</span>
        </>
      ) : (
        <>
          <span>
            {isCertificateDirty ? "Update & Next" : "Continue"}
          </span>

          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </>
      )}
    </button>
  )}

  {/* STEP 4 */}
  {currentStep === 4 && (
    <button
      onClick={handlePaymentDetailsNext}
      disabled={loading}
      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center space-x-2"
    >
      {loading ? (
        <span>Saving...</span>
      ) : (
        <>
          <span>Continue</span>

          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </>
      )}
    </button>
  )}

  {/* STEP 5 — SAVE */}
  {currentStep === 5 && (
    <button
      onClick={handleAdditionalDetailsSave}
      disabled={loading}
      className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center space-x-2"
    >
      {loading ? (
        <>
          <svg
            className="animate-spin w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
          </svg>
          <span>Saving...</span>
        </>
      ) : (
        <>
          <Save className="w-4 h-4" />
          <span>Save</span>
        </>
      )}
    </button>
  )}

</div>             
            </div>
          </div>
        </div>
      </div>
      
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {}}
        className="w-full max-w-md"
      >
        <div className="text-center p-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Course Updated Successfully!</h3>
          <p className="text-gray-600 mb-6">
            Your course "{courseData.title}" has been updated successfully.
          </p>
          <button
            onClick={handleGoToDashboard}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Go to Courses</span>
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default EditCourse